/**
 * Helpers for Bunny Stream videos referenced by stories.
 * A story's `videoLink` has the form `<libraryId>/<videoGuid>`; the CDN pull zone serves
 * MP4 renditions at `/<videoGuid>/play_<res>p.mp4` (CORS `*`, so they can be fetched as blobs).
 */
export const BUNNY_STREAM_CDN = 'https://vz-e462438a-03a.b-cdn.net';

/** Descending order: the first rendition that exists is the best available quality. */
export const VIDEO_RESOLUTIONS: number[] = [2160, 1440, 1080, 720, 480, 360, 240];

export const getVideoId = (videoLink?: string | null): string | null => {
    if (!videoLink) return null;
    const idx = videoLink.indexOf('/');
    const id = (idx >= 0 ? videoLink.slice(idx + 1) : videoLink).trim();
    return id || null;
};

export const renditionUrl = (videoId: string, resolution: number) =>
    `${BUNNY_STREAM_CDN}/${videoId}/play_${resolution}p.mp4`;

export const thumbnailUrl = (videoId: string) => `${BUNNY_STREAM_CDN}/${videoId}/thumbnail.jpg`;

export interface IBestRendition {
    resolution: number;
    url: string;
    size: number;
}

const bestRenditionCache = new Map<string, Promise<IBestRendition | null>>();

/** Probes renditions from highest to lowest with HEAD requests; result is cached per video. */
export const findBestRendition = (videoId: string): Promise<IBestRendition | null> => {
    const cached = bestRenditionCache.get(videoId);
    if (cached) return cached;
    const probe = (async () => {
        for (const resolution of VIDEO_RESOLUTIONS) {
            const url = renditionUrl(videoId, resolution);
            try {
                const res = await fetch(url, { method: 'HEAD' });
                if (res.ok) {
                    return { resolution, url, size: Number(res.headers.get('content-length')) || 0 };
                }
            } catch (e) {
                // network / CORS failure for this rendition: try the next one
            }
        }
        return null;
    })();
    bestRenditionCache.set(videoId, probe);
    probe.then(
        (value) => { if (!value) bestRenditionCache.delete(videoId); },
        () => bestRenditionCache.delete(videoId),
    );
    return probe;
};

export const formatBytes = (bytes: number): string => {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const sanitizeFileName = (value: string): string => {
    const cleaned = value.replace(/[^\w-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    return cleaned || 'story';
};

export const saveBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
};

export interface IDownloadProgress {
    loaded: number;
    total: number;
}

export interface IDownloadOptions {
    videoLink: string;
    /** Base for the file name, e.g. `story-8550-Amanda`; resolution + `.mp4` are appended. */
    fileBase: string;
    onProgress?: (progress: IDownloadProgress) => void;
    signal?: AbortSignal;
}

/** Downloads the best available rendition of a story video to the user's device. */
export const downloadBestQuality = async (options: IDownloadOptions): Promise<IBestRendition> => {
    const videoId = getVideoId(options.videoLink);
    if (!videoId) throw new Error('This story has no video');
    const best = await findBestRendition(videoId);
    if (!best) throw new Error('No downloadable video file was found for this story');

    const fileName = `${sanitizeFileName(options.fileBase)}_${best.resolution}p.mp4`;
    const res = await fetch(best.url, { signal: options.signal });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);

    const total = Number(res.headers.get('content-length')) || best.size || 0;
    let blob: Blob;
    if (res.body && typeof res.body.getReader === 'function') {
        const reader = res.body.getReader();
        const chunks: BlobPart[] = [];
        let loaded = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) {
                chunks.push(value as BlobPart);
                loaded += value.length;
                options.onProgress?.({ loaded, total });
            }
        }
        blob = new Blob(chunks, { type: 'video/mp4' });
    } else {
        blob = await res.blob();
        options.onProgress?.({ loaded: blob.size, total: blob.size });
    }
    saveBlob(blob, fileName);
    return best;
};
